import {
  OfferedOffer,
  OfferGroupSummary,
  offerApi,
} from '@texas/api/endpoints/offerApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { Locale } from '@texas/i18n/types';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OfferOverviewBranch, OfferOverviewRow } from './OfferOverviewTable';
import { offerEvents } from './events';

type OfferLifecycle = 'drafts' | 'sent' | 'accepted' | 'archived';

export function useOffers(variantId: number) {
  const { t } = useTranslation();
  const {
    currenciesData: [, refetchCurrencies],
  } = useMetadata();
  const {
    data: offerGroups,
    error: offerGroupsError,
    loading: offersLoading,
    refetch: refetchOffers,
  } = useApiResource(offerApi.getByVariant);
  const {
    data: offeredOffers,
    error: offeredOffersError,
    loading: offeredOffersLoading,
    refetch: refetchOfferedOffers,
  } = useApiResource(offerApi.getOfferedOffersByVariant);
  const [activeLifecycle, setActiveLifecycle] =
    useState<OfferLifecycle>('drafts');
  const [branchId, setBranchId] = useState<number | null>(null);
  const [searchInput, setSearchInput] = useState('');

  const refreshOffers = useCallback(() => {
    refetchOffers(variantId, true);
    refetchOfferedOffers(variantId);
  }, [refetchOffers, refetchOfferedOffers, variantId]);

  useEffect(() => {
    refetchCurrencies();
  }, [refetchCurrencies]);

  useEffect(() => {
    refreshOffers();
  }, [refreshOffers]);

  useEffect(() => offerEvents.onUpdated.sub(refreshOffers), [refreshOffers]);

  const groups = useMemo(() => offerGroups ?? [], [offerGroups]);
  const customerOffers = useMemo(() => offeredOffers ?? [], [offeredOffers]);
  const activeGroups = useMemo(
    () => groups.filter((group) => !group.archived),
    [groups],
  );
  const archivedGroups = useMemo(
    () => groups.filter((group) => group.archived),
    [groups],
  );
  const activeCustomerOffers = useMemo(
    () => customerOffers.filter((offer) => !offer.cancelledAt),
    [customerOffers],
  );
  const cancelledCustomerOffers = useMemo(
    () => customerOffers.filter((offer) => !!offer.cancelledAt),
    [customerOffers],
  );
  const acceptedCustomerOffers = useMemo(
    () => activeCustomerOffers.filter(isAcceptedOffer),
    [activeCustomerOffers],
  );
  const sentCustomerOffers = useMemo(
    () => activeCustomerOffers.filter((offer) => !isAcceptedOffer(offer)),
    [activeCustomerOffers],
  );

  const lifecycleItems = [
    {
      value: 'drafts' as const,
      label: t('offer.drafts'),
      count: activeGroups.length,
    },
    {
      value: 'sent' as const,
      label: t('offer.sent'),
      count: sentCustomerOffers.length,
    },
    {
      value: 'accepted' as const,
      label: t('general.accepted'),
      count: acceptedCustomerOffers.length,
    },
    {
      value: 'archived' as const,
      label: t('general.archived'),
      count: archivedGroups.length + cancelledCustomerOffers.length,
    },
  ];

  const branchOptions = useMemo(() => {
    const branches = new Map<number, string>();
    groups.forEach((group) =>
      [...group.coveredBranches, ...group.missingBranches].forEach((branch) =>
        branches.set(branch.branchId, branch.identifier),
      ),
    );
    customerOffers.forEach((offer) =>
      offer.branches.forEach((branch) =>
        branches.set(branch.branchId, branch.branchIdentifier),
      ),
    );
    return [...branches.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [customerOffers, groups]);

  const lifecycleRows = useMemo(() => {
    if (activeLifecycle === 'drafts') {
      return activeGroups.map((group) => getDraftRow(group, t));
    }
    if (activeLifecycle === 'sent') {
      return sentCustomerOffers.map((offer) => getCustomerOfferRow(offer, t));
    }
    if (activeLifecycle === 'accepted') {
      return acceptedCustomerOffers.map((offer) =>
        getCustomerOfferRow(offer, t),
      );
    }
    return [
      ...archivedGroups.map((group) => getDraftRow(group, t)),
      ...cancelledCustomerOffers.map((offer) => getCustomerOfferRow(offer, t)),
    ];
  }, [
    acceptedCustomerOffers,
    activeGroups,
    activeLifecycle,
    archivedGroups,
    cancelledCustomerOffers,
    sentCustomerOffers,
    t,
  ]);

  const rows = useMemo(() => {
    const normalizedSearch = searchInput.trim().toLocaleLowerCase();
    return lifecycleRows.filter(
      (row) =>
        (branchId == null ||
          row.branches.some((branch) => branch.id === branchId)) &&
        (!normalizedSearch || row.searchValue.includes(normalizedSearch)),
    );
  }, [branchId, lifecycleRows, searchInput]);

  return {
    activeLifecycle,
    branchId,
    branchOptions,
    error: offerGroupsError ?? offeredOffersError,
    lifecycleItems,
    loading: offersLoading || offeredOffersLoading,
    rows,
    searchInput,
    setActiveLifecycle,
    setBranchId,
    setSearchInput,
  };
}

function getDraftRow(group: OfferGroupSummary, t: TFunction): OfferOverviewRow {
  const totalPriceCount = group.branchCurrencies.reduce(
    (total, currency) => total + currency.totalPriceCount,
    0,
  );
  const completedPriceCount = group.branchCurrencies.reduce(
    (total, currency) => total + currency.offeredPriceCount,
    0,
  );
  const missingPriceCount = Math.max(0, totalPriceCount - completedPriceCount);
  const branches: OfferOverviewBranch[] = [
    ...group.coveredBranches.map((branch) => ({
      id: branch.branchId,
      identifier: branch.identifier,
      missing: false,
    })),
    ...group.missingBranches.map((branch) => ({
      id: branch.branchId,
      identifier: branch.identifier,
      missing: true,
    })),
  ];
  const readiness = group.archived
    ? t('general.archived')
    : totalPriceCount === 0
    ? t('offer.pricingInProgress')
    : missingPriceCount > 0
    ? t('offer.pricesMissing', { count: missingPriceCount })
    : t('offer.readyToSend');
  const readinessTone: OfferOverviewRow['readinessTone'] = group.archived
    ? 'muted'
    : totalPriceCount > 0 && missingPriceCount === 0
    ? 'success'
    : missingPriceCount > 0
    ? 'warning'
    : 'muted';
  const subtitle = group.offerNumbers.length
    ? t('offer.offerNumbersSummary', {
        numbers: group.offerNumbers.join(', '),
      })
    : t('offer.basedOnTargetTiers');

  return {
    id: `draft-${group.id}`,
    offerGroupId: group.id,
    title: t('offer.draftNumber', { id: group.id }),
    subtitle,
    branches,
    tiers: group.tiers.map((tier) => tier.quantity),
    owner: group.creatorName,
    updated: formatDate(Locale.En, group.created),
    readiness,
    readinessTone,
    badge: group.isMain
      ? t('offer.main')
      : group.archived
      ? t('general.archived')
      : undefined,
    badgeTone: group.isMain ? 'success' : 'muted',
    searchValue: [
      group.creatorName,
      ...group.offerNumbers,
      ...branches.map((branch) => branch.identifier),
    ]
      .join(' ')
      .toLocaleLowerCase(),
  };
}

function getCustomerOfferRow(
  offer: OfferedOffer,
  t: TFunction,
): OfferOverviewRow {
  const accepted = isAcceptedOffer(offer);
  const cancelled = !!offer.cancelledAt;
  const tiers = [
    ...new Set(
      offer.branches.flatMap((branch) =>
        branch.currencies.flatMap((currency) =>
          currency.tierPrices.map((tier) => tier.quantity),
        ),
      ),
    ),
  ].sort((a, b) => a - b);
  const branches = offer.branches.map((branch) => ({
    id: branch.branchId,
    identifier: branch.branchIdentifier,
    missing: false,
  }));

  return {
    id: `offer-${offer.id}`,
    offerGroupId: offer.offerGroupId,
    title: offer.offerNumber,
    subtitle: offer.customerName,
    branches,
    tiers,
    owner: offer.ownerName,
    updated: formatDate(Locale.En, offer.sentOn),
    readiness: cancelled
      ? t('offer.cancelled')
      : accepted
      ? t('general.accepted')
      : t('offer.sent'),
    readinessTone: accepted ? 'success' : cancelled ? 'warning' : 'muted',
    badge: cancelled ? t('offer.cancelled') : undefined,
    badgeTone: cancelled ? 'warning' : undefined,
    searchValue: [
      offer.offerNumber,
      offer.customerName,
      offer.ownerName,
      ...branches.map((branch) => branch.identifier),
    ]
      .join(' ')
      .toLocaleLowerCase(),
  };
}

function isAcceptedOffer(offer: OfferedOffer) {
  return (
    offer.tierPriceCount > 0 &&
    offer.acceptedTierPriceCount === offer.tierPriceCount
  );
}
